import { useState, useEffect } from "react";
import { X, Sliders } from "lucide-react";
import { getProfile, updateProfile, getCountries } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function DiscoveryPreferencesDialog({ open, onClose }) {
  const { user, refresh } = useAuth();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Preference states
  const [prefGender, setPrefGender] = useState("");
  const [prefMinAge, setPrefMinAge] = useState(18);
  const [prefMaxAge, setPrefMaxAge] = useState(99);
  const [prefCountry, setPrefCountry] = useState("");
  const [prefMaxDistance, setPrefMaxDistance] = useState(15000);
  const [prefHealthStatus, setPrefHealthStatus] = useState("");
  const [prefSexualOrientation, setPrefSexualOrientation] = useState("");

  // Load countries once
  useEffect(() => {
    getCountries().then(data => {
      if (Array.isArray(data)) {
        setCountries(data.map(c => c.name).sort());
      }
    }).catch(() => {});
  }, []);

  // Load existing preferences when dialog opens
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getProfile()
      .then(profile => {
        setPrefGender(profile.pref_gender || "");
        setPrefMinAge(profile.pref_min_age || 18);
        setPrefMaxAge(profile.pref_max_age || 99);
        setPrefCountry(profile.pref_country || "");
        setPrefMaxDistance(profile.pref_max_distance || 15000);
        setPrefHealthStatus(profile.pref_health_status || "");
        setPrefSexualOrientation(profile.pref_sexual_orientation || "");
      })
      .catch(() => toast.error("Failed to load preferences"))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile({
        pref_gender: prefGender,
        pref_min_age: prefMinAge,
        pref_max_age: prefMaxAge,
        pref_country: prefCountry,
        pref_max_distance: prefMaxDistance,
        pref_health_status: prefHealthStatus,
        pref_sexual_orientation: prefSexualOrientation,
      });
      await refresh();
      toast.success("Discovery preferences updated");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#E7E5E0]">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Sliders className="w-5 h-5" /> Discovery Preferences
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-[#F5F3EE] rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Gender */}
              <div>
                <label className="text-xs font-semibold uppercase text-gray-500">Gender</label>
                <select
                  value={prefGender}
                  onChange={(e) => setPrefGender(e.target.value)}
                  className="neo-input mt-1 w-full"
                >
                  <option value="">Any</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Non-binary">Non-binary</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Age Range */}
              <div>
                <label className="text-xs font-semibold uppercase text-gray-500">Age Range</label>
                <div className="flex items-center gap-3 mt-1">
                  <input
                    type="number"
                    min={18}
                    max={99}
                    value={prefMinAge}
                    onChange={(e) => setPrefMinAge(parseInt(e.target.value) || 18)}
                    className="neo-input w-20 text-center"
                  />
                  <span className="text-gray-400">to</span>
                  <input
                    type="number"
                    min={18}
                    max={99}
                    value={prefMaxAge}
                    onChange={(e) => setPrefMaxAge(parseInt(e.target.value) || 99)}
                    className="neo-input w-20 text-center"
                  />
                </div>
              </div>

              {/* Country */}
              <div>
                <label className="text-xs font-semibold uppercase text-gray-500">Country</label>
                <select
                  value={prefCountry}
                  onChange={(e) => setPrefCountry(e.target.value)}
                  className="neo-input mt-1 w-full"
                >
                  <option value="">Any</option>
                  {countries.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Max Distance */}
              <div>
                <label className="text-xs font-semibold uppercase text-gray-500">
                  Max Distance (km)
                </label>
                <input
                  type="number"
                  min={1}
                  max={15000}
                  value={prefMaxDistance}
                  onChange={(e) => setPrefMaxDistance(parseInt(e.target.value) || 15000)}
                  className="neo-input mt-1 w-full"
                />
              </div>

              {/* Health Status */}
              <div>
                <label className="text-xs font-semibold uppercase text-gray-500">Health Status</label>
                <select
                  value={prefHealthStatus}
                  onChange={(e) => setPrefHealthStatus(e.target.value)}
                  className="neo-input mt-1 w-full"
                >
                  <option value="">Any</option>
                  <option value="HIV">HIV</option>
                  <option value="HPV">HPV</option>
                  <option value="HSV">HSV</option>
                </select>
              </div>

              {/* Sexual Orientation */}
              <div>
                <label className="text-xs font-semibold uppercase text-gray-500">
                  Sexual Orientation
                </label>
                <select
                  value={prefSexualOrientation}
                  onChange={(e) => setPrefSexualOrientation(e.target.value)}
                  className="neo-input mt-1 w-full"
                >
                  <option value="">Any</option>
                  <option value="Straight">Straight</option>
                  <option value="Gay">Gay</option>
                  <option value="Lesbian">Lesbian</option>
                  <option value="Bisexual">Bisexual</option>
                </select>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-[#E7E5E0]">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-[#E7E5E0] rounded-xl text-sm font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="flex-1 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Preferences"}
          </button>
        </div>
      </div>
    </div>
  );
}